class VideoUtils {
    constructor() {
        // Initialize caches for players
        this.vimeoPlayers = new Map();
        this.youtubePlayers = new Map();
		this.videoMuteStatus = new Map(); // Track muted status per video/iframe
    }

    // Push video interaction to the GTM data layer
    pushVideoEventToDataLayer(eventAction, videoType, videoLabel) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "video_interaction",
            video_action: eventAction, // play, pause, progress, end
            video_type: videoType, // HTML5, Vimeo, YouTube
            video_label: videoLabel, // URL or title
        });
        //console.log(`Event pushed: ${eventAction}, Type: ${videoType}, Label: ${videoLabel}`);
    }

    // Enable YouTube API if not already enabled
    enableYouTubeAPI(iframe) {
        if (iframe.src.includes("youtube.com") && !iframe.src.includes("enablejsapi=1")) {
            iframe.src += (iframe.src.includes("?") ? "&" : "?") + "enablejsapi=1";
        }
    }

    // Load YouTube API dynamically
    loadYouTubeAPI() {
        return new Promise((resolve) => {
            if (window.YT && window.YT.Player) {
                resolve(); // API already loaded
            } else {
                const script = document.createElement("script");
                script.src = "https://www.youtube.com/iframe_api";
                script.async = true;

                window.onYouTubeIframeAPIReady = () => resolve(); // Wait for API to load
                document.head.appendChild(script);
            }
        });
    }

    // Initialize YouTube Player and cache it
    async initializeYouTubePlayer(iframe, eventHandlers) {
        await this.loadYouTubeAPI();

        const youtubePlayer = new YT.Player(iframe, {
            events: {
                onReady: eventHandlers.onReady || (() => {}),
                onStateChange: eventHandlers.onStateChange || (() => {}),
            },
        });

        this.youtubePlayers.set(iframe, youtubePlayer);
        return youtubePlayer;
    }

    // Setup progress tracking for both Vimeo and YouTube players
    setupProgressTracking(player, type, source) {
        const progressMarkers = { 25: false, 50: false, 75: false, 99: false };

        const trackProgress = (percentage) => {
            Object.keys(progressMarkers).forEach((marker) => {
                if (percentage >= marker && !progressMarkers[marker]) {
                    this.pushVideoEventToDataLayer(`progress_${marker}`, type, source);
                    progressMarkers[marker] = true;
                }
            });
        };

        if (type === "Vimeo") {
            player.on("timeupdate", (data) => {
                const percentage = (data.seconds / data.duration) * 100;
                trackProgress(percentage);
            });
        } else if (type === "YouTube") {
            const interval = setInterval(() => {
                const percentage = (player.getCurrentTime() / player.getDuration()) * 100;
                trackProgress(percentage);
                if (percentage >= 99) clearInterval(interval);
            }, 1000);
        }
    }

    // Handle FancyBox close and reset video playback
    setupFancyBoxCloseHandler(iframe, type, playButton = null, callback = null) {
        const player =
            type === "Vimeo"
                ? this.getVimeoPlayer(iframe)
                : this.getYouTubePlayer(iframe);

        $(document).on("afterClose.fb", async () => {
            try {
                if (player) {
                    type === "Vimeo" ? await player.play() : player.playVideo();
                    //console.log(`${type} video resumed after FancyBox closed.`);
                }

                if (playButton) {
                    playButton.textContent = "Pause";
                    playButton.classList.add("playing");
                }

                if (typeof callback === "function") callback();
            } catch (error) {
                console.error(`Error resuming ${type} video after FancyBox closed:`, error);
            }
        });
    }

    // Play and control mute for Vimeo Video
    async playAndControlVimeo(iframe, mute = false) {
        const player = this.getVimeoPlayer(iframe);

        try {
            await player.ready();
            const isPaused = await player.getPaused();
            await player.setVolume(mute ? 0 : 1);
            if (isPaused) await player.play();
        } catch (error) {
            console.error("Error controlling Vimeo video:", error);
        }

        this.setupProgressTracking(player, "Vimeo", iframe.src);
    }

    // Play YouTube video
    async playYouTube(iframe, eventHandlers = {}) {
        this.enableYouTubeAPI(iframe);
        const player = await this.initializeYouTubePlayer(iframe, eventHandlers);
        player.playVideo();
        this.setupProgressTracking(player, "YouTube", iframe.src);
    }

    // Pause Vimeo video
    async pauseVimeo(iframe) {
        const player = this.getVimeoPlayer(iframe);
        try {
            await player.pause();
        } catch (error) {
            console.error("Error pausing Vimeo video:", error);
        }
    }

    // Pause YouTube video
    async pauseYouTube(iframe) {
        const player = this.youtubePlayers.get(iframe);
        if (player) {
            try {
                player.pauseVideo();
            } catch (error) {
                console.error("Error pausing YouTube video:", error);
            }
        }
    }

	async isVimeoPlaying(iframe, maxAttempts = 10, interval = 200) {
		const player = this.getVimeoPlayer(iframe);

		try {
			await player.ready(); // Ensure the player is initialized
			let attempts = 0;

			while (attempts < maxAttempts) {
				const isPaused = await player.getPaused();

				if (!isPaused) {
					//console.log(`Vimeo video is playing (after ${attempts + 1} attempts): ${iframe.src}`);
					return true; // Video is playing
				}

				attempts++;
				await new Promise((resolve) => setTimeout(resolve, interval)); // Wait before retrying
			}

			//console.log(`Vimeo video is not playing after ${maxAttempts} attempts: ${iframe.src}`);
			return false; // Default to not playing if state doesn't stabilize
		} catch (error) {
			console.error("Error checking Vimeo play state:", error);
			return false;
		}
	}

    // Check if YouTube video is playing
    async isYouTubePlaying(iframe) {
        const player = this.youtubePlayers.get(iframe);
        if (player) {
            const state = player.getPlayerState();
            return state === YT.PlayerState.PLAYING;
        }
        return false;
    }

    // Get or create Vimeo player
    getVimeoPlayer(iframe) {
        if (!this.vimeoPlayers.has(iframe)) {
            const player = new Vimeo.Player(iframe);
            this.vimeoPlayers.set(iframe, player);
        }
        return this.vimeoPlayers.get(iframe);
    }

    // Get or create YouTube player
    getYouTubePlayer(iframe) {
        return this.youtubePlayers.get(iframe);
    }
}

const videoUtils = new VideoUtils();
export default videoUtils;
